import React, { useRef, useState, useCallback } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { Download, Share2, Instagram, Facebook, Twitter, Linkedin, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const SITE_URL = 'https://siddhivinayakinfra.estate';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_realestate-cms-15/artifacts/57kg0gau_DBK%20LOGO%20Final.png';

const SOCIAL_PLATFORMS = [
  { id: 'square', label: 'Instagram / Facebook', size: '1080×1080', w: 1080, h: 1080 },
  { id: 'landscape', label: 'Twitter / LinkedIn', size: '1200×675', w: 1200, h: 675 },
  { id: 'story', label: 'Stories / Reels', size: '1080×1920', w: 1080, h: 1920 },
];

function downloadCanvas(canvas, filename) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

export default function QRCodePage() {
  const [copied, setCopied] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('square');
  const [downloading, setDownloading] = useState(false);
  const qrHiddenRef = useRef(null);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(SITE_URL);
      setCopied(true);
      toast.success('URL copied to clipboard!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy URL');
    }
  }, []);

  const handleDownload = useCallback(async (platformId) => {
    const platform = SOCIAL_PLATFORMS.find(p => p.id === platformId);
    if (!platform) return;
    setDownloading(true);

    try {
      // Wait for hidden QR canvas to mount
      await new Promise(r => setTimeout(r, 300));
      const hiddenCanvas = qrHiddenRef.current?.querySelector('canvas');
      if (!hiddenCanvas) { toast.error('QR canvas not ready'); setDownloading(false); return; }

      const offscreen = document.createElement('canvas');
      offscreen.width = platform.w;
      offscreen.height = platform.h;
      const ctx = offscreen.getContext('2d');

      // === Background gradient ===
      const bg = ctx.createLinearGradient(0, 0, platform.w, platform.h);
      bg.addColorStop(0, '#0d1b2a');
      bg.addColorStop(0.5, '#1F3864');
      bg.addColorStop(1, '#0d1b2a');
      ctx.fillStyle = bg;
      ctx.roundRect(0, 0, platform.w, platform.h, 32);
      ctx.fill();

      // === Gold top accent bar ===
      const accentH = Math.round(platform.h * 0.008);
      ctx.fillStyle = '#C9A84C';
      ctx.fillRect(0, 0, platform.w, accentH);

      const centerX = platform.w / 2;

      // === Load logo ===
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      await new Promise((res, rej) => {
        logoImg.onload = res;
        logoImg.onerror = res; // non-fatal
        logoImg.src = LOGO_URL;
        setTimeout(res, 4000);
      });

      // === Layout by orientation ===
      if (platform.id === 'story') {
        // Portrait story layout
        const qrSize = Math.round(platform.w * 0.62);
        const qrX = (platform.w - qrSize) / 2;
        const qrY = Math.round(platform.h * 0.28);

        // Logo top
        if (logoImg.width) {
          const lH = Math.round(platform.h * 0.08);
          const lW = lH;
          ctx.drawImage(logoImg, centerX - lW / 2, Math.round(platform.h * 0.1), lW, lH);
        }

        // Company name
        ctx.fillStyle = '#C9A84C';
        ctx.font = `bold ${Math.round(platform.w * 0.055)}px Georgia, serif`;
        ctx.textAlign = 'center';
        ctx.fillText('DBK Group Of Companies', centerX, Math.round(platform.h * 0.22));

        ctx.fillStyle = 'rgba(255,255,255,0.65)';
        ctx.font = `${Math.round(platform.w * 0.033)}px sans-serif`;
        ctx.fillText('Siddhivinayak Infra', centerX, Math.round(platform.h * 0.255));

        // QR card
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.roundRect(qrX - 24, qrY - 24, qrSize + 48, qrSize + 48, 20);
        ctx.fill();
        ctx.drawImage(hiddenCanvas, 0, 0, hiddenCanvas.width, hiddenCanvas.height, qrX, qrY, qrSize, qrSize);

        // Scan label
        ctx.fillStyle = '#C9A84C';
        ctx.font = `bold ${Math.round(platform.w * 0.048)}px Georgia, serif`;
        ctx.fillText('Scan to Visit Our Website', centerX, qrY + qrSize + 72);

        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.font = `${Math.round(platform.w * 0.032)}px monospace`;
        ctx.fillText(SITE_URL, centerX, qrY + qrSize + 116);

        // Tagline
        ctx.fillStyle = 'rgba(201,168,76,0.7)';
        ctx.font = `italic ${Math.round(platform.w * 0.03)}px Georgia, serif`;
        ctx.fillText('Transforming Land Into Landmark Communities Since 2009', centerX, Math.round(platform.h * 0.9));

      } else {
        // Square / landscape layouts
        const isLandscape = platform.id === 'landscape';
        const qrSize = isLandscape ? Math.round(platform.h * 0.65) : Math.round(platform.w * 0.52);
        const qrX = isLandscape ? Math.round(platform.w * 0.07) : (platform.w - qrSize) / 2;
        const qrY = isLandscape ? (platform.h - qrSize) / 2 : Math.round(platform.h * 0.3);

        // QR white card
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.roundRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40, 16);
        ctx.fill();
        ctx.drawImage(hiddenCanvas, 0, 0, hiddenCanvas.width, hiddenCanvas.height, qrX, qrY, qrSize, qrSize);

        if (isLandscape) {
          // Right side text
          const tx = Math.round(platform.w * 0.52);
          const ty = Math.round(platform.h * 0.18);

          if (logoImg.width) {
            const lH = Math.round(platform.h * 0.14);
            ctx.drawImage(logoImg, tx, ty, lH, lH);
          }

          ctx.fillStyle = '#C9A84C';
          ctx.font = `bold ${Math.round(platform.h * 0.085)}px Georgia, serif`;
          ctx.textAlign = 'left';
          ctx.fillText('DBK Group', tx, Math.round(platform.h * 0.46));
          ctx.fillText('Of Companies', tx, Math.round(platform.h * 0.56));

          ctx.fillStyle = 'rgba(255,255,255,0.75)';
          ctx.font = `${Math.round(platform.h * 0.05)}px sans-serif`;
          ctx.fillText('Scan to visit our website', tx, Math.round(platform.h * 0.67));

          ctx.fillStyle = 'rgba(201,168,76,0.85)';
          ctx.font = `${Math.round(platform.h * 0.038)}px monospace`;
          ctx.fillText(SITE_URL, tx, Math.round(platform.h * 0.77));

        } else {
          // Square layout: text above and below
          ctx.textAlign = 'center';

          if (logoImg.width) {
            const lH = Math.round(platform.h * 0.1);
            ctx.drawImage(logoImg, centerX - lH / 2, Math.round(platform.h * 0.03), lH, lH);
          }

          ctx.fillStyle = '#C9A84C';
          ctx.font = `bold ${Math.round(platform.w * 0.046)}px Georgia, serif`;
          ctx.fillText('DBK Group Of Companies', centerX, Math.round(platform.h * 0.19));

          ctx.fillStyle = 'rgba(255,255,255,0.7)';
          ctx.font = `${Math.round(platform.w * 0.028)}px sans-serif`;
          ctx.fillText('Siddhivinayak Infra Estate', centerX, Math.round(platform.h * 0.225));

          ctx.fillStyle = '#C9A84C';
          ctx.font = `bold ${Math.round(platform.w * 0.038)}px Georgia, serif`;
          ctx.fillText('Scan to Visit Our Website', centerX, qrY + qrSize + 52);

          ctx.fillStyle = 'rgba(255,255,255,0.8)';
          ctx.font = `${Math.round(platform.w * 0.026)}px monospace`;
          ctx.fillText(SITE_URL, centerX, qrY + qrSize + 96);

          ctx.fillStyle = 'rgba(201,168,76,0.65)';
          ctx.font = `italic ${Math.round(platform.w * 0.024)}px Georgia, serif`;
          ctx.fillText('Transforming Land Into Landmark Communities Since 2009', centerX, Math.round(platform.h * 0.95));
        }
      }

      // Bottom gold accent
      ctx.fillStyle = '#C9A84C';
      ctx.fillRect(0, platform.h - accentH, platform.w, accentH);

      downloadCanvas(offscreen, `DBK-QRCode-${platform.id}-${platform.w}x${platform.h}.png`);
      toast.success(`Downloaded ${platform.label} (${platform.size})`);
    } catch (err) {
      console.error(err);
      toast.error('Download failed — try again');
    }
    setDownloading(false);
  }, []);

  const platform = SOCIAL_PLATFORMS.find(p => p.id === selectedPlatform);

  return (
    <div className="min-h-screen bg-[#0d1b2a] text-white font-sans flex flex-col items-center px-4 py-12 relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, #C9A84C 0%, transparent 50%), radial-gradient(circle at 80% 20%, #1F3864 0%, transparent 50%)' }} />

      {/* Header */}
      <div className="relative z-10 text-center mb-10">
        <img
          src={LOGO_URL}
          alt="DBK Group"
          className="w-20 h-20 object-contain mx-auto mb-4 drop-shadow-lg"
        />
        <h1 className="text-4xl sm:text-5xl font-bold text-[#C9A84C] tracking-tight"
          style={{ fontFamily: 'Georgia, serif' }}>
          DBK Group Of Companies
        </h1>
        <p className="text-white/60 mt-2 text-base">Siddhivinayak Infra Estate — QR Code Hub</p>
      </div>

      <div className="relative z-10 w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

        {/* Left: Live QR Preview */}
        <div className="flex flex-col items-center gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-2xl shadow-black/60 relative">
            <QRCodeSVG
              value={SITE_URL}
              size={280}
              level="H"
              data-testid="qr-code-svg"
              imageSettings={{
                src: LOGO_URL,
                height: 52,
                width: 52,
                excavate: true,
              }}
            />
            <div className="absolute inset-0 rounded-2xl ring-1 ring-[#C9A84C]/30 pointer-events-none" />
          </div>

          <div className="text-center">
            <p className="text-[#C9A84C] font-semibold text-sm tracking-widest uppercase mb-1">Scan to visit</p>
            <a
              href={SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm underline underline-offset-4"
              data-testid="qr-site-link"
            >
              {SITE_URL} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Copy URL */}
          <button
            onClick={handleCopy}
            data-testid="copy-url-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-sm font-medium transition-all">
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy URL'}
          </button>
        </div>

        {/* Right: Download Options */}
        <div className="flex flex-col gap-5">
          <h2 className="text-xl font-bold text-[#C9A84C]" style={{ fontFamily: 'Georgia, serif' }}>
            Download for Social Media
          </h2>
          <p className="text-white/55 text-sm">
            Each image is perfectly sized and branded for its platform — ready to post without editing.
          </p>

          <div className="space-y-3">
            {SOCIAL_PLATFORMS.map((p) => (
              <div key={p.id}
                className={`group flex items-center justify-between gap-4 px-4 py-3.5 rounded-xl border cursor-pointer transition-all ${selectedPlatform === p.id ? 'bg-[#C9A84C]/15 border-[#C9A84C]/60' : 'bg-white/5 border-white/10 hover:border-[#C9A84C]/40'}`}
                onClick={() => setSelectedPlatform(p.id)}
                data-testid={`platform-option-${p.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full border-2 transition-all ${selectedPlatform === p.id ? 'border-[#C9A84C] bg-[#C9A84C]' : 'border-white/30'}`} />
                  <div>
                    <p className="font-semibold text-sm text-white">{p.label}</p>
                    <p className="text-xs text-white/45">{p.size} px</p>
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload(p.id); }}
                  disabled={downloading}
                  data-testid={`download-btn-${p.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#C9A84C]/20 hover:bg-[#C9A84C]/40 border border-[#C9A84C]/40 text-[#C9A84C] text-xs font-semibold transition-all disabled:opacity-50">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </button>
              </div>
            ))}
          </div>

          {/* Download all */}
          <button
            onClick={async () => { for (const p of SOCIAL_PLATFORMS) { await handleDownload(p.id); await new Promise(r => setTimeout(r, 400)); } }}
            disabled={downloading}
            data-testid="download-all-btn"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-[#C9A84C] hover:bg-[#b8922f] text-[#0d1b2a] font-bold text-sm tracking-wide transition-all disabled:opacity-50">
            <Download className="w-4 h-4" />
            {downloading ? 'Preparing…' : 'Download All 3 Sizes'}
          </button>

          {/* Social share direct links */}
          <div>
            <p className="text-white/45 text-xs mb-3 uppercase tracking-widest">Quick Share</p>
            <div className="flex gap-3 flex-wrap">
              {[
                { label: 'Facebook', icon: Facebook, color: '#1877F2', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SITE_URL)}` },
                { label: 'Twitter/X', icon: Twitter, color: '#1DA1F2', href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(SITE_URL)}&text=${encodeURIComponent('Explore DBK Group Of Companies — Premium Real Estate in Maharashtra')}` },
                { label: 'LinkedIn', icon: Linkedin, color: '#0A66C2', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SITE_URL)}` },
                { label: 'WhatsApp', icon: Share2, color: '#25D366', href: `https://api.whatsapp.com/send?text=${encodeURIComponent('Explore DBK Group Of Companies — Premium Real Estate: ' + SITE_URL)}` },
              ].map(({ label, icon: Icon, color, href }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  data-testid={`share-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white/80 hover:text-white border border-white/10 hover:border-white/30 transition-all bg-white/5 hover:bg-white/10">
                  <Icon className="w-3.5 h-3.5" style={{ color }} />
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden high-res canvas for rendering */}
      <div className="hidden" ref={qrHiddenRef}>
        <QRCodeCanvas
          value={SITE_URL}
          size={800}
          level="H"
          imageSettings={{
            src: LOGO_URL,
            height: 130,
            width: 130,
            excavate: true,
          }}
        />
      </div>

      {/* Footer note */}
      <p className="relative z-10 mt-12 text-white/30 text-xs text-center">
        All images are high-resolution PNG files ready for social media posting.
        The QR code contains error correction level H for maximum scan reliability.
      </p>
    </div>
  );
}
